"use client";

import {
    ISeriesApi,
    LineData,
    LineSeries as LineSeriesConst, // 导入系列构造器
    LineSeriesPartialOptions,
    WhitespaceData,
} from "lightweight-charts";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { useChart } from "./context";

export interface LineSeriesProps {
    data: (LineData | WhitespaceData)[];
    options?: LineSeriesPartialOptions;
    // 用于在外部追加数据
    appendData?: LineData;
}

/**
 * 折线图系列组件
 */
export const LineSeries = forwardRef<ISeriesApi<"Line"> | null, LineSeriesProps>(
    ( { data, options, appendData }, ref ) => {
        const { chart } = useChart();
        const seriesRef = useRef<ISeriesApi<"Line"> | null>( null );

        useEffect( () => {
            if ( !chart ) return;

            // 创建折线系列
            const series = chart.addSeries( LineSeriesConst, {
                color: "#265CFF",
                lineWidth: 2,
                crosshairMarkerVisible: true,
                ...options,
            } );

            series.setData( data );
            seriesRef.current = series;

            return () => {
                if ( chart ) {
                    // 组件卸载时移除系列
                    try {
                        chart.removeSeries( series );
                    } catch ( e ) {
                        // 忽略可能的错误
                    }
                }
            };
        }, [ chart ] );

        // 响应配置变化
        useEffect( () => {
            if ( seriesRef.current && options ) {
                seriesRef.current.applyOptions( options );
            }
        }, [ options ] );

        // 响应数据全量更新
        useEffect( () => {
            if ( seriesRef.current ) {
                seriesRef.current.setData( data );
            }
        }, [ data ] );

        // 响应数据追加
        useEffect( () => {
            if ( seriesRef.current && appendData ) {
                seriesRef.current.update( appendData );
            }
        }, [ appendData ] );

        // 暴露系列实例给外部
        useImperativeHandle( ref, () => seriesRef.current!, [ seriesRef.current ] );

        return null; // 此组件不渲染 DOM
    },
);

LineSeries.displayName = "LineSeries";
